package com.starparty.app.vector_maths

import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.sin

class Matrix4d {
    var m00: Double
    var m01: Double
    var m02: Double
    var m03: Double
    var m10: Double
    var m11: Double
    var m12: Double
    var m13: Double
    var m20: Double
    var m21: Double
    var m22: Double
    var m23: Double
    var m30: Double
    var m31: Double
    var m32: Double
    var m33: Double

    constructor() {
        m00 = 0.0
        m01 = 0.0
        m02 = 0.0
        m03 = 0.0
        m10 = 0.0
        m11 = 0.0
        m12 = 0.0
        m13 = 0.0
        m20 = 0.0
        m21 = 0.0
        m22 = 0.0
        m23 = 0.0
        m30 = 0.0
        m31 = 0.0
        m32 = 0.0
        m33 = 0.0
    }

    constructor(
        m00: Double,
        m01: Double,
        m02: Double,
        m03: Double,
        m10: Double,
        m11: Double,
        m12: Double,
        m13: Double,
        m20: Double,
        m21: Double,
        m22: Double,
        m23: Double,
        m30: Double,
        m31: Double,
        m32: Double,
        m33: Double
    ) {
        this.m00 = m00
        this.m01 = m01
        this.m02 = m02
        this.m03 = m03
        this.m10 = m10
        this.m11 = m11
        this.m12 = m12
        this.m13 = m13
        this.m20 = m20
        this.m21 = m21
        this.m22 = m22
        this.m23 = m23
        this.m30 = m30
        this.m31 = m31
        this.m32 = m32
        this.m33 = m33
    }

    constructor(v: DoubleArray) {
        m00 = v[0]
        m01 = v[1]
        m02 = v[2]
        m03 = v[3]
        m10 = v[4]
        m11 = v[5]
        m12 = v[6]
        m13 = v[7]
        m20 = v[8]
        m21 = v[9]
        m22 = v[10]
        m23 = v[11]
        m30 = v[12]
        m31 = v[13]
        m32 = v[14]
        m33 = v[15]
    }

    fun setIdentity() {
        m00 = 1.0
        m01 = 0.0
        m02 = 0.0
        m03 = 0.0
        m10 = 0.0
        m11 = 1.0
        m12 = 0.0
        m13 = 0.0
        m20 = 0.0
        m21 = 0.0
        m22 = 1.0
        m23 = 0.0
        m30 = 0.0
        m31 = 0.0
        m32 = 0.0
        m33 = 1.0
    }

    fun translate(x: Double, y: Double, z: Double) {
        val t = Matrix4d()
        t.setIdentity()
        t.m30 = x
        t.m31 = y
        t.m32 = z

        mul(t)
    }



    fun setScale(scale: Double) {
        val tmp_rot = DoubleArray(9)
        val tmp_scale = DoubleArray(3)
        getScaleRotate(tmp_scale, tmp_rot)
        m00 = tmp_rot[0] * scale
        m01 = tmp_rot[1] * scale
        m02 = tmp_rot[2] * scale
        m10 = tmp_rot[3] * scale
        m11 = tmp_rot[4] * scale
        m12 = tmp_rot[5] * scale
        m20 = tmp_rot[6] * scale
        m21 = tmp_rot[7] * scale
        m22 = tmp_rot[8] * scale

    }

    private fun getScaleRotate(scales: DoubleArray, rots: DoubleArray) {
        val tmp = doubleArrayOf(
            m00,
            m01,
            m02,
            m10,
            m11,
            m12,
            m20,
            m21,
            m22
        )
        Matrix3d.Companion.compute_svd(tmp, scales, rots)
    }

    fun mul(scalar: Double) {
        m00 *= scalar
        m01 *= scalar
        m02 *= scalar
        m03 *= scalar
        m10 *= scalar
        m11 *= scalar
        m12 *= scalar
        m13 *= scalar
        m20 *= scalar
        m21 *= scalar
        m22 *= scalar
        m23 *= scalar
        m30 *= scalar
        m31 *= scalar
        m32 *= scalar
        m33 *= scalar
    }

    fun mul(m1: Matrix4d?) {
        val m00 =
            m00 * m1!!.m00 + m01 * m1.m10 + m02 * m1.m20 + m03 * m1.m30
        val m01 =
            this.m00 * m1.m01 + m01 * m1.m11 + m02 * m1.m21 + m03 * m1.m31
        val m02 =
            this.m00 * m1.m02 + this.m01 * m1.m12 + m02 * m1.m22 + m03 * m1.m32
        val m03 =
            this.m00 * m1.m03 + this.m01 * m1.m13 + this.m02 * m1.m23 + m03 * m1.m33
        val m10 =
            m10 * m1.m00 + m11 * m1.m10 + m12 * m1.m20 + m13 * m1.m30
        val m11 =
            this.m10 * m1.m01 + m11 * m1.m11 + m12 * m1.m21 + m13 * m1.m31
        val m12 =
            this.m10 * m1.m02 + this.m11 * m1.m12 + m12 * m1.m22 + m13 * m1.m32
        val m13 =
            this.m10 * m1.m03 + this.m11 * m1.m13 + this.m12 * m1.m23 + m13 * m1.m33
        val m20 =
            m20 * m1.m00 + m21 * m1.m10 + m22 * m1.m20 + m23 * m1.m30
        val m21 =
            this.m20 * m1.m01 + m21 * m1.m11 + m22 * m1.m21 + m23 * m1.m31
        val m22 =
            this.m20 * m1.m02 + this.m21 * m1.m12 + m22 * m1.m22 + m23 * m1.m32
        val m23 =
            this.m20 * m1.m03 + this.m21 * m1.m13 + this.m22 * m1.m23 + m23 * m1.m33
        val m30 =
            m30 * m1.m00 + m31 * m1.m10 + m32 * m1.m20 + m33 * m1.m30
        val m31 =
            this.m30 * m1.m01 + m31 * m1.m11 + m32 * m1.m21 + m33 * m1.m31
        val m32 =
            this.m30 * m1.m02 + this.m31 * m1.m12 + m32 * m1.m22 + m33 * m1.m32
        val m33 =
            this.m30 * m1.m03 + this.m31 * m1.m13 + this.m32 * m1.m23 + m33 * m1.m33
        this.m00 = m00
        this.m01 = m01
        this.m02 = m02
        this.m03 = m03
        this.m10 = m10
        this.m11 = m11
        this.m12 = m12
        this.m13 = m13
        this.m20 = m20
        this.m21 = m21
        this.m22 = m22
        this.m23 = m23
        this.m30 = m30
        this.m31 = m31
        this.m32 = m32
        this.m33 = m33
    }

    fun rotX(angle: Double) {
        val sinAngle = sin(angle)
        val cosAngle = cos(angle)
        m00 = 1.0
        m01 = 0.0
        m02 = 0.0
        m03 = 0.0
        m10 = 0.0
        m11 = cosAngle
        m12 = -sinAngle
        m13 = 0.0
        m20 = 0.0
        m21 = sinAngle
        m22 = cosAngle
        m23 = 0.0
        m30 = 0.0
        m31 = 0.0
        m32 = 0.0
        m33 = 1.0
    }

    fun rotY(angle: Double) {
        val sinAngle = sin(angle)
        val cosAngle = cos(angle)
        m00 = cosAngle
        m01 = 0.0
        m02 = sinAngle
        m03 = 0.0
        m10 = 0.0
        m11 = 1.0
        m12 = 0.0
        m13 = 0.0
        m20 = -sinAngle
        m21 = 0.0
        m22 = cosAngle
        m23 = 0.0
        m30 = 0.0
        m31 = 0.0
        m32 = 0.0
        m33 = 1.0
    }

    fun rotZ(angle: Double) {
        val sinAngle = sin(angle)
        val cosAngle = cos(angle)
        m00 = cosAngle
        m01 = -sinAngle
        m02 = 0.0
        m03 = 0.0
        m10 = sinAngle
        m11 = cosAngle
        m12 = 0.0
        m13 = 0.0
        m20 = 0.0
        m21 = 0.0
        m22 = 1.0
        m23 = 0.0
        m30 = 0.0
        m31 = 0.0
        m32 = 0.0
        m33 = 1.0
    }

    fun set(m1: Matrix4d?) {
        m00 = m1!!.m00
        m01 = m1.m01
        m02 = m1.m02
        m03 = m1.m03
        m10 = m1.m10
        m11 = m1.m11
        m12 = m1.m12
        m13 = m1.m13
        m20 = m1.m20
        m21 = m1.m21
        m22 = m1.m22
        m23 = m1.m23
        m30 = m1.m30
        m31 = m1.m31
        m32 = m1.m32
        m33 = m1.m33
    }

    fun invert(m1: Matrix4d) {
        invertGeneral(m1)
    }

    fun invert() {
        invertGeneral(this)
    }

    fun invertGeneral(m1: Matrix4d) {
        val result = DoubleArray(16)
        val row_perm = IntArray(4)
        val tmp = doubleArrayOf(
            m1.m00,
            m1.m01,
            m1.m02,
            m1.m03,
            m1.m10,
            m1.m11,
            m1.m12,
            m1.m13,
            m1.m20,
            m1.m21,
            m1.m22,
            m1.m23,
            m1.m30,
            m1.m31,
            m1.m32,
            m1.m33
        )
        if (!luDecomposition(tmp, row_perm)) {
            throw RuntimeException("SingularMatrixException")
        } else {
            for (i in 0..15) {
                result[i] = 0.0
            }
            result[0] = 1.0
            result[5] = 1.0
            result[10] = 1.0
            result[15] = 1.0
            luBacksubstitution(tmp, row_perm, result)
            m00 = result[0]
            m01 = result[1]
            m02 = result[2]
            m03 = result[3]
            m10 = result[4]
            m11 = result[5]
            m12 = result[6]
            m13 = result[7]
            m20 = result[8]
            m21 = result[9]
            m22 = result[10]
            m23 = result[11]
            m30 = result[12]
            m31 = result[13]
            m32 = result[14]
            m33 = result[15]
        }
    }

    companion object {
        private const val EPS = 1.0E-10
        fun luDecomposition(
            matrix0: DoubleArray,
            row_perm: IntArray
        ): Boolean {
            val row_scale = DoubleArray(4)
            var i = 0
            var imax = 0
            var j: Int
            var big: Double
            j = 4
            while (j-- != 0) {
                big = 0.0
                var var4 = 4
                while (var4-- != 0) {
                    var temp = matrix0[i++]
                    temp = abs(temp)
                    if (temp > big) {
                        big = temp
                    }
                }
                if (big == 0.0) {
                    return false
                }
                row_scale[imax++] = 1.0 / big
            }
            val mtx = 0
            j = 0
            while (j < 4) {
                var target: Int
                var p2: Int
                var sum: Double
                var k: Int
                var p1: Int
                i = 0
                while (i < j) {
                    target = mtx + 4 * i + j
                    sum = matrix0[target]
                    k = i
                    p1 = mtx + 4 * i
                    p2 = mtx + j
                    while (k-- != 0) {
                        sum -= matrix0[p1] * matrix0[p2]
                        ++p1
                        p2 += 4
                    }
                    matrix0[target] = sum
                    ++i
                }
                big = 0.0 // originally double big = 0.0D;
                imax = -1
                var temp: Double
                i = j
                while (i < 4) {
                    target = mtx + 4 * i + j
                    sum = matrix0[target]
                    k = j
                    p1 = mtx + 4 * i
                    p2 = mtx + j
                    while (k-- != 0) {
                        sum -= matrix0[p1] * matrix0[p2]
                        ++p1
                        p2 += 4
                    }
                    matrix0[target] = sum
                    if (row_scale[i] * abs(sum).also { temp = it } >= big) {
                        big = temp
                        imax = i
                    }
                    ++i
                }
                if (imax < 0) {
                    throw RuntimeException()
                }
                if (j != imax) {
                    k = 4
                    p1 = mtx + 4 * imax
                    p2 = mtx + 4 * j
                    while (k-- != 0) {
                        temp = matrix0[p1]
                        matrix0[p1++] = matrix0[p2]
                        matrix0[p2++] = temp
                    }
                    row_scale[imax] = row_scale[j]
                }
                row_perm[j] = imax
                if (matrix0[mtx + 4 * j + j] == 0.0) {
                    return false
                }
                if (j != 3) {
                    temp = 1.0 / matrix0[mtx + 4 * j + j]
                    target = mtx + 4 * (j + 1) + j
                    i = 3 - j
                    while (i-- != 0) {
                        matrix0[target] *= temp
                        target += 4
                    }
                }
                ++j
            }
            return true
        }

        fun luBacksubstitution(
            matrix1: DoubleArray,
            row_perm: IntArray,
            matrix2: DoubleArray
        ) {
            val rp = 0
            for (k in 0..3) {
                var ii = -1
                var rv: Int
                for (i in 0..3) {
                    val ip = row_perm[rp + i]
                    var sum = matrix2[k + 4 * ip]
                    matrix2[k + 4 * ip] = matrix2[k + 4 * i]
                    if (ii >= 0) {
                        rv = i * 4
                        for (j in ii..i - 1) {
                            sum -= matrix1[rv + j] * matrix2[k + 4 * j]
                        }
                    } else if (sum != 0.0) {
                        ii = i
                    }
                    matrix2[k + 4 * i] = sum
                }
                rv = 12 // originally int rv = 12;
                matrix2[k + 12] /= matrix1[rv + 3]
                rv = rv - 4
                matrix2[k + 8] =
                    (matrix2[k + 8] - matrix1[rv + 3] * matrix2[k + 12]) / matrix1[rv + 2]
                rv -= 4
                matrix2[k + 4] =
                    (matrix2[k + 4] - matrix1[rv + 2] * matrix2[k + 8] - matrix1[rv + 3] * matrix2[k + 12]) / matrix1[rv + 1]
                rv -= 4
                matrix2[k + 0] =
                    (matrix2[k + 0] - matrix1[rv + 1] * matrix2[k + 4] - matrix1[rv + 2] * matrix2[k + 8] - matrix1[rv + 3] * matrix2[k + 12]) / matrix1[rv + 0]
            }
        }
    }
}